import { UserStatus, PlatformRole } from "../config/types";
import AvatarWithFallback from "../molecules/AvatarWithFallback";
import { useUserContext } from "../hooks/useUserContext";
import { StatusBadge } from "../molecules/StatusBadge";
import { type User } from "../api/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import MoreTooltip from "../molecules/MoreTooltip";
import { useTranslation } from "react-i18next";
import { SquarePen, Trash, UserCheck, UserX } from "lucide-react";
import { Button } from "~/atoms/button";

interface Props {
  users: User[];
  onEditUser: (user: User) => void;
  onToggleUserStatus: (user: User) => void;
  onDeleteUser: (user: User) => void;
}

export const UserManagementTable = ({
  users,
  onEditUser,
  onToggleUserStatus,
  onDeleteUser,
}: Props) => {
  const { t } = useTranslation();
  const { user: currentUser } = useUserContext();

  return (
    <Table className="bg-card border-border border">
      <TableHeader className="bg-muted">
        <TableRow>
          <TableHead>{t(($) => $.commons.user.name)}</TableHead>
          <TableHead>{t(($) => $.commons.user.email)}</TableHead>
          <TableHead>{t(($) => $.commons.document.category)}</TableHead>
          <TableHead>{t(($) => $.commons.user.position)}</TableHead>
          <TableHead>{t(($) => $.commons.role)}</TableHead>
          <TableHead>{t(($) => $.commons.document.status)}</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>

      <TableBody>
        {users.map((user, index) => {
          const allCategories = user?.categories?.map((d) => d.title);
          const isActive = user.status === UserStatus.active;

          return (
            <TableRow key={`${user.id}_${index}`}>
              <TableCell>
                <AvatarWithFallback user={user} size="xs" withName />
              </TableCell>

              <TableCell>
                <div className="truncate">{user.email}</div>
              </TableCell>

              <TableCell>
                <div className="flex items-center gap-1">
                  <span>{allCategories[0] ?? "-"}</span>
                  <MoreTooltip data={allCategories} />
                </div>
              </TableCell>

              <TableCell>
                {user.position
                  ? user.position.length > 25
                    ? `${user?.position.slice(0, 25)}...`
                    : user.position
                  : "-"}
              </TableCell>

              <TableCell>{t(($) => $.commons.roles[user.role])}</TableCell>

              <TableCell>
                <StatusBadge.UserStatus status={user.status} />
              </TableCell>

              <TableCell className="text-muted-foreground flex justify-end">
                {currentUser.role === PlatformRole.superAdmin && (
                  <>
                    <Button
                      onClick={() => onEditUser(user)}
                      variant="ghost"
                      size="icon"
                    >
                      <SquarePen />
                    </Button>
                    <Button
                      onClick={() => onToggleUserStatus(user)}
                      disabled={user.status === UserStatus.pending}
                      variant="ghost"
                      size="icon"
                      className={
                        isActive
                          ? "hover:text-destructive"
                          : "hover:text-success"
                      }
                    >
                      {isActive ? <UserX /> : <UserCheck />}
                    </Button>
                    <Button
                      onClick={() => onDeleteUser(user)}
                      variant="ghost"
                      size="icon"
                      disabled={isActive}
                    >
                      <Trash />
                    </Button>
                  </>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
